import { useDevisActif } from '../hooks/useDevisActif';
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SearchModal from './SearchModal';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();
  // Iter 904 — la barre du bas (smartphone) ouvre/ferme ce même menu ; changement de page = menu fermé
  useEffect(() => {
    const toggle = () => setIsMenuOpen(o => !o);
    window.addEventListener('raav-toggle-menu', toggle);
    return () => window.removeEventListener('raav-toggle-menu', toggle);
  }, []);
  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);
  // Iter 270 (Kamel — test iPhone) : accès permanent au guide d'installation PWA
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  const openInstallGuide = () => {
    setIsMenuOpen(false);
    window.dispatchEvent(new CustomEvent('raav-open-install'));
  };

  const isActive = (path) => location.pathname === path;

  const devis = useDevisActif();
  const navLinks = [
    { path: '/', label: 'ACCUEIL' },
    { path: '/a-propos', label: 'QUI SOMMES-NOUS' },
    { path: '/voyages', label: 'NOS VOYAGES', highlight: true },
    { path: '/informations-administratives', label: 'INFOS PRATIQUES' },
    ...(devis.actif ? [{ path: '/simulateur-espagne', label: 'DEVIS ESPAGNE' }] : []),
    { path: '/inscription', label: "S'INSCRIRE", highlight: true },
    { path: '/payer', label: 'RÉGLER', highlight: true },
    { path: '/moi', label: 'MON ESPACE' },
    { path: '/voyages-passes', label: 'RÉTROSPECTIVE' },
    { path: '/actualites', label: 'ACTUALITÉS' },
    { path: '/restez-informe', label: '📬 RESTEZ INFORMÉ', highlight: true },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: 'CONTACT' },
  ];

  return (
    <header className="sticky top-0 z-50 shadow-lg" data-testid="main-header">
      {/* Barre haute : Logo + Nom + WhatsApp */}
      <div className="bg-[#79c9ea]">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between py-2.5">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img
              src="/images/logo.jpg"
              alt="Rhône Alpes Amitié Voyages"
              className="h-12 w-12 rounded-full border-2 border-white/50 shadow-sm object-cover"
            />
            <div className="hidden sm:block">
              <span className="text-white font-bold text-base leading-tight block">Rhône Alpes Amitié</span>
              <span className="text-white/80 text-xs leading-tight">Pour voyager seul, entre amis ou en famille</span>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <a
              href="https://wa.me/message/XTYPH2HUY3L6L1"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 min-h-[44px] rounded-full text-sm font-semibold hover:bg-[#1fb855] transition shadow-sm"
              data-testid="whatsapp-link"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <span className="hidden sm:inline">WhatsApp</span>
            </a>

            {/* Search Button */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 text-white hover:bg-white/20 rounded-full transition"
              data-testid="search-btn"
              aria-label="Rechercher"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden flex items-center gap-1.5 px-3 py-2 text-white hover:bg-white/20 rounded-lg transition"
              data-testid="mobile-menu-btn"
            >
              {isMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
              <span className="text-sm font-bold uppercase">{isMenuOpen ? 'Fermer' : 'Menu'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Barre de navigation desktop */}
      <nav className="hidden lg:block bg-white border-b border-gray-200" data-testid="desktop-nav">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-1 py-1.5 flex-wrap">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.path.replace(/\//g, '') || 'home'}`}
              className={`px-3 py-1.5 rounded-lg text-xs tracking-wide transition-all whitespace-nowrap ${
                isActive(link.path)
                  ? 'bg-[#1E3A5F] text-white shadow-sm font-bold'
                  : link.highlight
                    ? 'bg-[#79c9ea] text-white font-bold shadow-md hover:bg-[#5ba8c8] hover:shadow-lg hover:scale-105'
                    : 'text-[#1E3A5F]/70 font-bold hover:bg-[#79c9ea]/15 hover:text-[#1E3A5F]'
              }`}
            >
              {link.label}
            </Link>
          ))}
          {!isStandalone && (
            <button onClick={openInstallGuide}
              className="px-3 py-1.5 rounded-lg text-xs tracking-wide whitespace-nowrap bg-emerald-600 text-white font-bold shadow-md hover:bg-emerald-700 transition-all"
              data-testid="nav-install-app">
              📲 INSTALLER L'APP
            </button>
          )}
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <nav className="lg:hidden bg-white border-b border-gray-200 shadow-lg max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-nav">
          <div className="max-w-7xl mx-auto px-3 py-3 grid grid-cols-2 gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`min-h-[48px] flex items-center px-3 rounded-xl text-xs font-bold tracking-wide transition-all ${
                  isActive(link.path)
                    ? 'bg-[#1E3A5F] text-white'
                    : link.highlight
                      ? 'bg-[#79c9ea] text-white shadow-md'
                      : 'bg-gray-50 text-[#1E3A5F]/80 hover:bg-[#79c9ea]/15'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {!isStandalone && (
              <button onClick={openInstallGuide}
                className="col-span-2 min-h-[48px] px-3 rounded-xl text-xs font-bold tracking-wide text-left bg-emerald-600 text-white shadow-md"
                data-testid="mobile-nav-install-app">
                📲 INSTALLER L'APPLICATION
              </button>
            )}
          </div>
        </nav>
      )}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </header>
  );
};

export default Header;
